import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import { PopperPlacementType } from "@mui/base/Popper";
import { IterableMap } from "@redotech/react-util/component";
import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useSearch } from "@redotech/react-util/search";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import {
  patchCustomerTag,
  postCustomerTag,
  setCustomerTags,
} from "@redotech/redo-merchant-app-common/client/customer-tags";
import {
  getBadgePropsForCustomerTag,
  getIconForCustomerTagProvider,
} from "@redotech/redo-merchant-app-common/customer-tags";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  areCustomerTagsEqual,
  CustomerTagWithId,
  isCustomerTagImmutableByRedo,
} from "@redotech/redo-model/customer";
import { CustomerTagProvider } from "@redotech/redo-model/customer-tags/customer-tag-record";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  RedoList,
  RedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { Divider } from "@redotech/redo-web/divider";
import { Dropdown } from "@redotech/redo-web/dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { sinkPromise } from "@redotech/util/promise";
import classNames from "classnames";
import Fuse from "fuse.js";
import {
  Dispatch,
  memo,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getConversation } from "../client/conversations";
import { ActiveConversationContext } from "../context/active-conversation-context";
import * as conversationTagInputCss from "../conversation-tags/conversation-tag-input.module.css";
import {
  BadgePillThemeToColorMapping,
  ConversationTagPill,
} from "../conversation-tags/conversation-tag-pill";
import { CreateTagModal } from "../conversation-tags/create-tag-modal";
import { EditTagModal } from "../conversation-tags/edit-tag-modal";
import { CustomerTagsContext } from "../services/customer-tags-services";

const searcher = new Fuse<CustomerTagWithId>([], {
  keys: ["name"],
  threshold: 0.3,
});

export const CustomerTagInput = memo(function CustomerTagInput({
  customerId,
  currentTags,
  setCurrentTags,
  disabled = false,
  showBorder = true,
  onlyDropdownMode = undefined,
  showAddButtonToEnterInput: showAddButtonToEnterInput = false,
  dropdownPlacement = "bottom",
}: {
  customerId: string;
  currentTags: CustomerTagWithId[];
  setCurrentTags(tags: CustomerTagWithId[]): void;
  disabled?: boolean;
  showBorder?: boolean;
  onlyDropdownMode?: {
    anchor: HTMLElement | null;
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
  };
  showAddButtonToEnterInput?: boolean;
  dropdownPlacement?: PopperPlacementType;
}) {
  const [availableTags, setAvailableTags] = useState<CustomerTagWithId[]>([]);

  const team = useRequiredContext(TeamContext);
  const client = useRequiredContext(RedoMerchantClientContext);
  const { setActiveConversation, activeConversation } = useContext(
    ActiveConversationContext,
  );
  const canEditCustomer = useHasPermission(SupportPermissions.EDIT_CUSTOMER);

  const [customerTagsLoad, reloadCustomerTags] =
    useLazyContext(CustomerTagsContext);

  const [customerTags] = useLazyContext(CustomerTagsContext);

  useEffect(() => {
    setAvailableTags(
      customerTags.value?.filter((tag) => !isCustomerTagImmutableByRedo(tag)) ??
        [],
    );
  }, [customerTags]);

  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );

  const selectedTags: string[] = useMemo(() => {
    return currentTags.map((tag) => tag.tagId);
  }, [currentTags]);

  const [searchText, setSearchText] = useState<string>("");

  const [selectDropdownOpen_, setSelectDropdownOpen_] = useState(false);

  const [selectDropdownOpen, setSelectDropdownOpen] = onlyDropdownMode
    ? [onlyDropdownMode.open, onlyDropdownMode.setOpen]
    : [selectDropdownOpen_, setSelectDropdownOpen_];

  const [tagBeingEdited, setTagBeingEdited] = useState<
    CustomerTagWithId | undefined
  >();

  const [createTagModalOpen, setCreateTagModalOpen] = useState(false);

  const filteredAvailableTags = useSearch(searcher, availableTags, searchText);

  function addTagToCustomer(tag: CustomerTagWithId) {
    if (currentTags.some((usedTag) => areCustomerTagsEqual(usedTag, tag))) {
      return;
    }
    // Eagerly update
    const newTags = [...currentTags, tag];
    sinkPromise(setCustomerTags(client, { customerId, tags: newTags }));
    setCurrentTags(newTags);
    setSearchText("");
  }

  function removeTagFromCustomer(tag: CustomerTagWithId) {
    // Eagerly update
    const updatedTags = currentTags.filter(
      (current) => !areCustomerTagsEqual(current, tag),
    );
    sinkPromise(setCustomerTags(client, { customerId, tags: updatedTags }));
    setCurrentTags(updatedTags);
  }

  const addNewTagButton = (
    <Flex
      className={conversationTagInputCss.pointer}
      onClick={() => setSelectDropdownOpen(true)}
    >
      <RedoBadge
        color="gray"
        dataTestId="add-customer-tag-button"
        segmentLeading={{ type: "icon", Icon: PlusIcon }}
        size="xs"
        text={currentTags.length === 0 ? "Add tag" : undefined}
      />
    </Flex>
  );

  const existingTags = currentTags.length > 0 && (
    <Flex className={conversationTagInputCss.tagsFlex} wrap="wrap">
      <IterableMap items={currentTags} keyFn={(value) => value.name}>
        {(tag) => (
          <ConversationTagPill
            badgeSize="xs"
            iconTrailing={((Icon) =>
              Icon ? { type: "icon", Icon, size: "sm" } : undefined)(
              getIconForCustomerTagProvider(tag.provider),
            )}
            tag={tag}
            xClicked={
              disabled || isCustomerTagImmutableByRedo(tag)
                ? undefined
                : () => {
                    removeTagFromCustomer(tag);
                  }
            }
          />
        )}
      </IterableMap>
      {showAddButtonToEnterInput && currentTags.length === 0 && addNewTagButton}
    </Flex>
  );

  useEffect(() => {
    if (selectDropdownOpen) {
      inputRef?.focus();
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectDropdownOpen]);

  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);

  const tagInput = (
    <input
      autoFocus
      className={classNames(
        conversationTagInputCss.input,
        showBorder && conversationTagInputCss.border,
        !selectDropdownOpen &&
          showAddButtonToEnterInput &&
          conversationTagInputCss.hide,
      )}
      disabled={disabled}
      onChange={(event) => setSearchText(event.target.value)}
      onClick={(event) => {
        event.stopPropagation();
        setSelectDropdownOpen(true);
      }}
      onFocus={(event) => {
        event.stopPropagation();
        setSelectDropdownOpen(true);
      }}
      placeholder="Search tags..."
      ref={setInputRef}
      value={searchText}
    />
  );

  const wrappedInput = (
    <Flex className={conversationTagInputCss.inputFlex} ref={setDropdownAnchor}>
      <div className={conversationTagInputCss.inputWrapper}>
        {existingTags}
        {showAddButtonToEnterInput && addNewTagButton}
      </div>
    </Flex>
  );

  const tagItems = useMemo(() => {
    const tagItems: RedoListItem<CustomerTagWithId | "create">[] =
      filteredAvailableTags.map<RedoListItem<CustomerTagWithId | "create">>(
        (tag) => ({
          id: tag.tagId,
          value: tag,
          type: "badge",
          badge: {
            ...getBadgePropsForCustomerTag(tag),
            color: BadgePillThemeToColorMapping[tag.pillTheme],
          },
          menu: {
            onClick: () => {
              setTagBeingEdited(tag);
              setSelectDropdownOpen(false);
            },
          },
        }),
      );

    if (canEditCustomer) {
      const createTagItem: RedoListItem<"create"> = {
        id: "create",
        value: "create",
        type: "text",
        text: "New tag",
        leadingItem: { type: "icon", Icon: PlusIcon },
      };

      tagItems.push(createTagItem);
    }

    return tagItems;
  }, [canEditCustomer, filteredAvailableTags, setSelectDropdownOpen]);

  const handleItemSelected = useHandler(
    ({
      item,
      selected,
    }: {
      item: RedoListItem<CustomerTagWithId | "create">;
      selected: boolean;
    }) => {
      if (item.value === "create") {
        setCreateTagModalOpen(true);
        setSelectDropdownOpen(false);
      } else {
        const tag = item.value;
        if (selected) {
          addTagToCustomer(tag);
        } else {
          removeTagFromCustomer(tag);
        }
      }
    },
  );

  const [focusedIndex, setFocusedIndex] = useState<number | undefined>();

  const dropdownItems = (
    <Dropdown
      anchor={onlyDropdownMode?.anchor || dropdownAnchor}
      fitToAnchor={false}
      open={selectDropdownOpen}
      placement={dropdownPlacement}
    >
      <Flex
        className={conversationTagInputCss.dropdownContainer}
        dir="column"
        gap="sm"
      >
        {tagInput}
        <Divider />
        <RedoList
          focusedIndex={focusedIndex}
          items={tagItems}
          itemSelected={handleItemSelected}
          itemsLoading={customerTags.pending}
          refToListenTo={inputRef}
          selectedItems={selectedTags}
          setFocusedIndex={setFocusedIndex}
          size="sm"
        />
      </Flex>
    </Dropdown>
  );

  async function handleEdit({
    tagId,
    name,
    pillTheme,
  }: {
    tagId: string;
    name: string;
    pillTheme: PillTheme;
  }) {
    await patchCustomerTag(client, tagId, { name, pillTheme }, async () => {
      reloadCustomerTags();
      if (activeConversation) {
        const updatedConversation = await getConversation(client, {
          conversationId: activeConversation._id,
        });
        setActiveConversation(updatedConversation);
      }
    });
  }

  const dropdown = selectDropdownOpen && (
    <ClickAwayListener onClickAway={() => setSelectDropdownOpen(false)}>
      {dropdownItems}
    </ClickAwayListener>
  );

  const editModal = tagBeingEdited && (
    <EditTagModal
      disableNameEdit
      handleEdit={handleEdit}
      resolved={() => {
        setTagBeingEdited(undefined);
        setSelectDropdownOpen(true);
      }}
      tag={tagBeingEdited}
      tags={customerTagsLoad}
    />
  );

  async function handleSubmit({
    name,
    pillTheme,
    provider,
  }: {
    name: string;
    pillTheme: PillTheme;
    provider: CustomerTagProvider;
  }) {
    const externalTagType =
      provider === CustomerTagProvider.COMMENTSOLD ? "custom" : undefined;
    const newTag = await postCustomerTag(
      client,
      { name, pillTheme, teamId: team._id, provider, externalTagType },
      reloadCustomerTags,
    );
    const newTagTyped: CustomerTagWithId = {
      name: newTag.name,
      pillTheme: newTag.pillTheme,
      tagId: newTag._id,
      source: newTag.source,
      provider: newTag.provider,
    };
    setSelectDropdownOpen(true);
    setCreateTagModalOpen(false);
    setAvailableTags([...availableTags, newTagTyped]);
  }

  const createModal = createTagModalOpen && (
    <CreateTagModal
      cancelClicked={() => {
        setSelectDropdownOpen(true);
        setCreateTagModalOpen(false);
      }}
      handleCreateTag={({
        name,
        pillTheme,
        provider,
      }: {
        name: string;
        pillTheme: PillTheme;
        provider: CustomerTagProvider;
      }) => handleSubmit({ name, pillTheme, provider })}
      showProviderSelector
      tagsLoad={customerTagsLoad}
    />
  );

  return (
    <>
      {!onlyDropdownMode ? (
        <div className={conversationTagInputCss.fullWidth}>
          {wrappedInput}
          {dropdown}
          {editModal}
          {createModal}
        </div>
      ) : (
        <>
          {dropdown}
          {editModal}
          {createModal}
        </>
      )}
    </>
  );
});
